import React from 'react';
import { renderEstadoBadge } from '../../utils/cotizacionHelpers';

/**
 * Header de detalle de cotización con hero, métricas y acciones.
 *
 * @param {Object} props
 * @param {Object} props.cotizacion - Datos de la cotización
 * @param {number} props.total - Total calculado de la cotización
 * @param {Function} props.onBack - Volver a la lista de cotizaciones
 * @param {Function} props.onDownloadPdf - Descargar PDF
 * @param {Function} props.onEnviarEmail - Enviar cotización por email
 * @param {Function} props.onCrearRevision - Crear nueva revisión
 * @param {boolean} props.pdfLoading - Estado de carga del PDF
 * @param {boolean} props.emailLoading - Estado de carga del email
 * @param {boolean} props.canDownloadPdf - Permiso para descargar PDF
 * @param {boolean} props.canSendEmail - Permiso para enviar email
 * @param {boolean} props.canCreateRevision - Estado + permiso para crear revisión
 * @returns {JSX.Element}
 */
function CotizacionHeader({
  cotizacion,
  total,
  onBack,
  onDownloadPdf,
  onEnviarEmail,
  onCrearRevision,
  pdfLoading,
  emailLoading,
  canDownloadPdf,
  canSendEmail,
  canCreateRevision
}) {
  return (
    <>
      <div className="cotizacion-hero">
        <div className="display-flex margin-bottom-md">
          <button className="btn btn-secondary" onClick={onBack}>
            <i className="fas fa-arrow-left"></i> Volver
          </button>
        </div>

        <div className="cotizacion-hero__header margin-bottom-md">
          <div className="cotizacion-hero__title-row">
            <h1 className="cotizacion-hero__title">{cotizacion.numero}</h1>
            {cotizacion.revision > 0 && (
              <span className="badge badge-info">Revisión {cotizacion.revision}</span>
            )}
            {renderEstadoBadge(cotizacion.estado)}
          </div>
          <div className="cotizacion-hero__actions">
            {canDownloadPdf && (
              <button className="btn btn-primary" onClick={onDownloadPdf} disabled={pdfLoading}>
                {pdfLoading
                  ? <><i className="fas fa-spinner fa-spin"></i> Generando...</>
                  : <><i className="fas fa-file-pdf"></i> Descargar PDF</>}
              </button>
            )}
            {canSendEmail && (
              <button
                className="btn btn-secondary"
                onClick={onEnviarEmail}
                disabled={emailLoading || !cotizacion.cliente?.email}
                title={!cotizacion.cliente?.email ? 'El cliente no tiene email registrado' : 'Enviar cotización por email al cliente'}
              >
                {emailLoading
                  ? <><i className="fas fa-spinner fa-spin"></i> Enviando...</>
                  : <><i className="fas fa-envelope"></i> Enviar por Email</>}
              </button>
            )}
            {canCreateRevision && (
              <button className="btn btn-warning" onClick={onCrearRevision}>
                <i className="fas fa-copy"></i> Nueva Revisión
              </button>
            )}
          </div>
        </div>

        <div className="cotizacion-hero__metrics">
          <div className="metric-item">
            <div className="text-secondary font-semibold margin-bottom-xs">Cliente</div>
            <div>
              <strong>{cotizacion.cliente?.razon_social}</strong>
              <div className="text-muted text-sm">{cotizacion.cliente?.rut}</div>
            </div>
          </div>
          <div className="metric-item">
            <div className="text-secondary font-semibold margin-bottom-xs">Total</div>
            <div className="cotizacion-hero__value">${total.toLocaleString('es-CL')}</div>
          </div>
          <div className="metric-item">
            <div className="text-secondary font-semibold margin-bottom-xs">Fecha Emisión</div>
            <div>{new Date(cotizacion.fecha_emision).toLocaleDateString('es-CL')}</div>
          </div>
          <div className="metric-item">
            <div className="text-secondary font-semibold margin-bottom-xs">Vencimiento</div>
            <div>{cotizacion.fecha_vencimiento ? new Date(cotizacion.fecha_vencimiento).toLocaleDateString('es-CL') : 'Sin vencimiento'}</div>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="cotizacion-info-grid">
          <div>
            <div className="text-secondary font-semibold margin-bottom-xs">Cliente</div>
            <div>
              <strong>{cotizacion.cliente?.razon_social}</strong>
              <div className="text-muted">{cotizacion.cliente?.rut}</div>
            </div>
          </div>
          <div>
            <div className="text-secondary font-semibold margin-bottom-xs">Estado</div>
            <div>{renderEstadoBadge(cotizacion.estado)}</div>
          </div>
          <div>
            <div className="text-secondary font-semibold margin-bottom-xs">Fecha Emisión</div>
            <div>{new Date(cotizacion.fecha_emision).toLocaleDateString('es-CL')}</div>
          </div>
          <div>
            <div className="text-secondary font-semibold margin-bottom-xs">Fecha Vencimiento</div>
            <div>{cotizacion.fecha_vencimiento ? new Date(cotizacion.fecha_vencimiento).toLocaleDateString('es-CL') : '-'}</div>
          </div>
          <div>
            <div className="text-secondary font-semibold margin-bottom-xs">Periodo Arriendo</div>
            <div>
              {cotizacion.fecha_inicio_arriendo && cotizacion.fecha_fin_arriendo
                ? `${new Date(cotizacion.fecha_inicio_arriendo).toLocaleDateString('es-CL')} - ${new Date(cotizacion.fecha_fin_arriendo).toLocaleDateString('es-CL')}`
                : '-'}
            </div>
          </div>
          <div>
            <div className="text-secondary font-semibold margin-bottom-xs">Descripción</div>
            <div>{cotizacion.descripcion || '-'}</div>
          </div>
        </div>
      </div>
    </>
  );
}

export default CotizacionHeader;
